import { AxiosRequestConfig } from "axios";
import { Filter, PagedResponse, Pagination, ResponseEnvelope } from ".";
import type { TaskExecution } from "../features/task";
import type { ExecutionState } from "../model/Process";
import type {
  ProcessExecution,
  TaskNotificationEvent,
} from "../model/ProcessExecution";
import type { AssignedRoleUserDto } from "../types";
import API, { responseOf } from "./base";

export const start = (
  processId: string,
  configuration: Partial<ProcessExecution>,
): Promise<ProcessExecution> =>
  responseOf(API.post(`/process-executions/${processId}/start`, configuration));

export const submitTask = (
  processExecutionId: string,
  taskId: string,
  taskExecution: TaskExecution,
): Promise<TaskExecution> =>
  responseOf(
    API.post(
      `/process-executions/${processExecutionId}/tasks/${taskId}/submit`,
      taskExecution,
    ),
  );

export const completeTask = (
  processExecutionId: string,
  taskId: string,
): Promise<TaskExecution> =>
  responseOf(
    API.post(
      `/process-executions/${processExecutionId}/tasks/${taskId}/complete`,
    ),
  );

export const getTaskTrackingInfo = (
  processExecutionId: string,
  taskId: string,
): Promise<TaskNotificationEvent[]> =>
  responseOf(
    API.get(
      `/process-executions/${processExecutionId}/task/${taskId}/tracking`,
    ),
  );

export const complete = async (processExecutionId: string): Promise<void> => {
  await API.post(`/process-executions/${processExecutionId}/complete`);
};

export const getTaskExecutions = (
  processExecutionId: string,
): Promise<TaskExecution[]> =>
  responseOf(
    API.get(`/process-executions/${processExecutionId}/taskExecutions`),
  );

export const getTaskExecution = (
  processExecutionId: string,
  taskId: string,
): Promise<TaskExecution> =>
  responseOf(
    API.get(`/process-executions/${processExecutionId}/tasks/${taskId}`),
  );

export const bySubjectId = (subjectId: string): Promise<ProcessExecution[]> =>
  responseOf(API.get(`/process-executions/bySubjectId/${subjectId}`));

export const byUserId = async (
  userId: string,
  pagination?: Pagination,
  hide?: string[],
): Promise<ResponseEnvelope<ProcessExecution[]>> => {
  const result = await API.get(`/process-executions/byUserId/${userId}`, {
    params: { hide, ...pagination },
  });
  return result.data as ResponseEnvelope<ProcessExecution[]>;
};

export const byUserIdAndState = (
  userId: string,
  state: ExecutionState,
  organizationId?: string,
  pagination?: Pagination,
  hide?: string[],
): Promise<ProcessExecution[]> =>
  responseOf(
    API.get(`/process-executions/byUserId/${userId}`, {
      params: { hide, organizationId, ...pagination, state },
    }),
  );

export const byOrganizationId = async (
  organizationId: string,
  filters?: Filter[],
  pagination?: Pagination,
  config?: AxiosRequestConfig,
): Promise<PagedResponse<ProcessExecution>> => {
  const result = await API.get(
    `/process-executions/byOrganizationId/${organizationId}`,
    {
      params: { filters, ...pagination },
      ...config,
    },
  );
  return (
    config?.format === "xlsx" ? result.data : result.data.payload
  ) as PagedResponse<ProcessExecution>;
};

export const byOrganizationThreads = async (
  organizationId: string,
  filters?: Filter[],
  pagination?: Pagination,
  config?: AxiosRequestConfig,
): Promise<PagedResponse<ProcessExecution>> => {
  const result = await API.get(
    `/process-executions/byOrganizationId/${organizationId}/byThread`,
    {
      params: { filters, ...pagination },
      ...config,
    },
  );
  return (
    config?.format === "xlsx" ? result.data : result.data.payload
  ) as PagedResponse<ProcessExecution>;
};

export const getDto = (processExecutionId: string): Promise<ProcessExecution> =>
  responseOf(API.get(`/process-executions/${processExecutionId}/dto`));

export const getStepExecutions = (
  processExecutionId: string,
): Promise<ProcessExecution[]> =>
  responseOf(API.get(`/process-executions/${processExecutionId}/steps`));

export const getAssignedRoleUsers = (
  processExecutionId: string,
): Promise<AssignedRoleUserDto[]> =>
  responseOf(
    API.get(`/process-executions/${processExecutionId}/assignedRoleUsers`),
  );

export const deleteProcessExecution = async (
  processExecutionId: string,
): Promise<void> => {
  await API.delete(`/process-executions/${processExecutionId}`);
};

export const retryErrorTask = async (
  processExecutionId: string,
  taskId: string,
): Promise<void> => {
  await API.post(
    `/process-executions/${processExecutionId}/tasks/${taskId}/retryErrorTask`,
  );
};

export const update = (
  processExecutionId: string,
  processExecution: Partial<ProcessExecution>,
): Promise<ProcessExecution> =>
  responseOf(
    API.put(`/process-executions/${processExecutionId}`, processExecution),
  );

export const assign = (
  processExecutionId: string,
  taskId: string,
  userId: string,
): Promise<ProcessExecution> =>
  responseOf(
    API.post(
      `/process-executions/${processExecutionId}/tasks/${taskId}/assign/${userId}`,
    ),
  );

export const unassign = async (
  processExecutionId: string,
  taskId: string,
): Promise<void> => {
  await API.post(
    `/process-executions/${processExecutionId}/tasks/${taskId}/unassign`,
  );
};

export const assignProcessExecution = (
  processExecutionId: string,
  userId: string,
): Promise<ProcessExecution> =>
  responseOf(
    API.post(`/process-executions/${processExecutionId}/assign/${userId}`),
  );

export const getUnreadCount = (organizationId: string): Promise<number> =>
  responseOf(API.get(`/process-executions/unread/${organizationId}`));

export const getCurrentTaskNotificationEvents = (
  processExecutionId: string,
): Promise<TaskNotificationEvent[]> =>
  responseOf(
    API.get(`/process-executions/${processExecutionId}/notificationEvents`),
  );
